import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Badge,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assessment as SalesIcon,
  AccountBalance as CommissionIcon,
  Timeline as TrendsIcon,
  NotificationsActive as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';

const RepNavigation = ({ children, currentUser, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/rep/dashboard' },
    { text: 'My Sales', icon: <SalesIcon />, path: '/rep/sales' },
    { text: 'Commission Tracker', icon: <CommissionIcon />, path: '/rep/commission' },
    { text: 'Sales Trends', icon: <TrendsIcon />, path: '/rep/trends' }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const getPageTitle = () => {
    const currentPath = location.pathname;
    const currentItem = menuItems.find(item => item.path === currentPath);
    return currentItem?.text || 'Rep Dashboard';
  };

  // Calculate commission status
  const commissionPercentage = currentUser?.currentMonthSales 
    ? Math.min((currentUser.currentMonthSales / currentUser.commissionThreshold) * 100, 100)
    : 0;

  const isCommissionEarned = currentUser?.currentMonthSales >= currentUser?.commissionThreshold;

  const drawerContent = (
    <Box sx={{ width: 280, height: '100%', bgcolor: '#0d9488', color: 'white' }}>
      {/* Logo Section */}
      <Box sx={{ 
        p: 3, 
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
          UnisynHub Rep
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
          Sales Portal
        </Typography>
      </Box>

      {/* User Info Section */}
      <Box sx={{ 
        p: 3, 
        bgcolor: '#0f766e',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: 'rgba(255, 255, 255, 0.2)', 
              color: 'white',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              mr: 2 
            }}
          >
            {currentUser?.name?.split(' ').map(n => n[0]).join('') || 'R'}
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
              {currentUser?.name || 'Rep User'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {currentUser?.territory || 'Sales Rep'}
            </Typography>
          </Box>
        </Box>
        
        {/* Enhanced Commission Status */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.875rem' }}>
              Commission Progress
            </Typography>
            <Chip
              size="small"
              label={isCommissionEarned ? `${currentUser?.commissionRate}%` : 'Pending'}
              sx={{
                bgcolor: isCommissionEarned ? 'rgba(16, 185, 129, 0.9)' : 'rgba(245, 158, 11, 0.9)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.75rem',
                border: isCommissionEarned ? '1px solid #10b981' : '1px solid #f59e0b',
                boxShadow: isCommissionEarned 
                  ? '0 2px 8px rgba(16, 185, 129, 0.3)' 
                  : '0 2px 8px rgba(245, 158, 11, 0.3)'
              }}
            />
          </Box>
          
          {/* Progress Bar */}
          <Box sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.15)', 
            borderRadius: 2, 
            height: 12, 
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ 
              background: isCommissionEarned 
                ? 'linear-gradient(90deg, #10b981, #16a34a)'
                : 'linear-gradient(90deg, #f59e0b, #d97706)',
              height: '100%',
              width: `${Math.min(commissionPercentage, 100)}%`,
              transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              borderRadius: 1,
              boxShadow: isCommissionEarned 
                ? '0 2px 8px rgba(16, 185, 129, 0.4)' 
                : '0 2px 8px rgba(245, 158, 11, 0.4)'
            }} />
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
              ${currentUser?.currentMonthSales?.toLocaleString() || '0'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 500 }}>
              ${currentUser?.commissionThreshold?.toLocaleString() || '200,000'}
            </Typography>
          </Box>
          
          <Typography variant="caption" sx={{ 
            color: isCommissionEarned ? '#dcfce7' : 'rgba(255, 255, 255, 0.8)', 
            fontWeight: 600,
            display: 'block',
            textAlign: 'center',
            mt: 1
          }}>
            {isCommissionEarned ? '🎉 Commission Earned!' : `${commissionPercentage.toFixed(1)}% Complete`}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Items */}
      <List sx={{ py: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem
              key={item.text}
              button
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                mx: 2,
                mb: 1,
                borderRadius: 2,
                bgcolor: isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                '&:hover': {
                  bgcolor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                },
                border: isActive ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
                transition: 'all 0.3s ease'
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: isActive ? 600 : 400,
                  color: 'white'
                }}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - 280px)` },
          ml: { md: `280px` },
          bgcolor: '#0f766e',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: 'white', fontWeight: 600 }}>
            {getPageTitle()}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" sx={{ mr: 1 }}>
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            
            <Typography variant="body2" sx={{ mr: 2, color: 'white', opacity: 0.9, display: { xs: 'none', sm: 'block' }, fontWeight: 600 }}>
              {currentUser?.name || 'Rep User'}
            </Typography>
            <IconButton
              color="inherit"
              onClick={handleUserMenuOpen}
              sx={{ p: 0 }}
            >
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', border: '2px solid rgba(255, 255, 255, 0.3)' }}>
                {currentUser?.name?.split(' ').map(n => n[0]).join('') || 'R'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem onClick={handleUserMenuClose}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          Profile Settings
        </MenuItem>
        <MenuItem onClick={handleUserMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Preferences
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            handleUserMenuClose();
            onLogout();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            border: 'none',
            boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
            zIndex: 1200
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
            border: 'none'
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - 280px)` },
          ml: { md: '280px' },
          mt: '64px',
          bgcolor: '#f8fafc',
          minHeight: 'calc(100vh - 64px)',
          overflow: 'auto'
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default RepNavigation;